/**
 * Comparison table field registry with conditional visibility.
 *
 * Each field has a `condition` that controls which property contexts it
 * appears in. Contexts (RED-267):
 * - 'apartment' — a building part is selected (KOM-like subject)
 * - 'built'     — built property without an apartment part (house etc.)
 * - 'land'      — unbuilt land parcel (evaluation object_type 'cadaster-id')
 *
 * Conditions:
 * - ALL           — every context
 * - BUILDING      — apartment + built (hidden for land)
 * - NON_APARTMENT — built + land (the RED-164 maatüki pindala group,
 *                 ordered second after location since the Elamu põhi fix)
 * - LAND          — land only (RED-267 land elements)
 *
 * The `buildComparisonTableConfig()` function filters fields by condition and
 * produces the exact same `markedTableConf` structure that the transaction
 * store uses. Apartment/built output is byte-identical to the pre-RED-267
 * binary hasBuildingPart behaviour.
 */

const ALL = 'all'
const BUILDING = 'building'
const NON_APARTMENT = 'non_apartment'
const LAND = 'land'

const CONDITION_CONTEXTS = {
  [ALL]: ['apartment', 'built', 'land'],
  [BUILDING]: ['apartment', 'built'],
  [NON_APARTMENT]: ['built', 'land'],
  [LAND]: ['land'],
}

export const COMPARISON_FIELD_REGISTRY = [
  // Price & time
  // RED-297: editable — a comparable is often sold bundled (apartment +
  // panipaik + parking) while the registry records the apartment price only.
  // The override is evaluation-scoped (changes pivot); transaction.tehingusumma
  // is never written. Deliberately NOT masterEditable: the master column has no
  // price and the docx replaces its cell with prose.
  { key: 'tehingusumma', condition: ALL, editable: true },
  { key: 'm2hind', condition: ALL },
  { key: 'aol_kpv', condition: ALL },
  { key: 'ajal_kohandus', condition: ALL, editable: true, numeric: true },
  { key: 'ajal_hind', condition: ALL },

  // Location (Asukoht)
  { key: 'aadress', condition: ALL, group: true, editable: true, masterEditable: true },
  { key: 'ad_link', condition: ALL },
  { key: 'asukoha_vordlus', condition: ALL, editable: true },
  { key: 'asukoha_kohandus', condition: ALL, editable: true, numeric: true, kohandus: true },

  // Juurdepääs (land only, RED-267)
  { key: 'juurdepaas', condition: LAND, group: true, editable: true, masterEditable: true },
  { key: 'juurdepaas_vordlus', condition: LAND, editable: true },
  { key: 'juurdepaas_kohandus', condition: LAND, editable: true, numeric: true, kohandus: true },

  // Land plot area (non-apartment). Sits right after location: the valuers'
  // report prose lists "Kinnistu suurus" as the second element of comparison
  // and the table follows that order (helpdesk, Anu Vinter, 2026-09).
  { key: 'maatuki_pindala', condition: NON_APARTMENT, group: true, masterEditable: true },
  { key: 'maatuki_pindala_vordlus', condition: NON_APARTMENT, editable: true },
  { key: 'maatuki_pindala_kohandus', condition: NON_APARTMENT, editable: true, numeric: true, kohandus: true },

  // Building construction
  { key: 'kandekonstruktsioon', condition: BUILDING, group: true, editable: true, masterEditable: true },
  { key: 'kandekonstruktsioon_vordlus', condition: BUILDING, editable: true },
  { key: 'kandekonstruktsioon_kohandus', condition: BUILDING, editable: true, numeric: true, kohandus: true },

  // Building age
  { key: 'ehitise_esmane_kasutus', condition: BUILDING, group: true, editable: true, masterEditable: true },
  { key: 'ehitise_seisukord', condition: BUILDING, masterEditable: true },
  { key: 'ehitise_vordlus', condition: BUILDING, editable: true },
  { key: 'ehitise_kohandus', condition: BUILDING, editable: true, numeric: true, kohandus: true },

  // Property area
  { key: 'vara_pind', condition: BUILDING, group: true, masterEditable: true },
  { key: 'pindala_vordlus', condition: BUILDING, editable: true },
  { key: 'pindala_kohandus', condition: BUILDING, editable: true, numeric: true, kohandus: true },

  // Ehitusõigus (land only, RED-267)
  { key: 'ehitusoigus', condition: LAND, group: true, editable: true, masterEditable: true },
  { key: 'ehitusoigus_vordlus', condition: LAND, editable: true },
  { key: 'ehitusoigus_kohandus', condition: LAND, editable: true, numeric: true, kohandus: true },

  // Rooms
  { key: 'hooneosa_tubade_arv', condition: BUILDING, group: true, editable: true, masterEditable: true },
  { key: 'tubade_vordlus', condition: BUILDING, editable: true },
  { key: 'tubade_kohandus', condition: BUILDING, editable: true, numeric: true, kohandus: true },

  // Unit condition
  { key: 'hooneosa_seisukord', condition: BUILDING, group: true, masterEditable: true },
  { key: 'hooneosa_vordlus', condition: BUILDING, editable: true },
  { key: 'hooneosa_kohandus', condition: BUILDING, editable: true, numeric: true, kohandus: true },

  // Floors
  { key: 'korruste_arv', condition: BUILDING, group: true, editable: true, masterEditable: true },
  { key: 'korruste_vordlus', condition: BUILDING, editable: true },
  { key: 'korruste_kohandus', condition: BUILDING, editable: true, numeric: true, kohandus: true },

  // Engineering networks
  { key: 'tehnovorgud', condition: ALL, group: true, editable: true, masterEditable: true },
  { key: 'tehnovorgud_vordlus', condition: ALL, editable: true },
  { key: 'tehnovorgud_kohandus', condition: ALL, editable: true, numeric: true, kohandus: true },

  // Haljastus (land only, RED-267)
  { key: 'haljastus', condition: LAND, group: true, editable: true, masterEditable: true },
  { key: 'haljastus_vordlus', condition: LAND, editable: true },
  { key: 'haljastus_kohandus', condition: LAND, editable: true, numeric: true, kohandus: true },

  // Heating
  { key: 'hooneosa_soojavarustuse_liik', condition: BUILDING, group: true, editable: true, masterEditable: true },
  { key: 'hooneosa_soojavarustuse_vordlus', condition: BUILDING, editable: true },
  { key: 'hooneosa_soojavarustuse_kohandus', condition: BUILDING, editable: true, numeric: true, kohandus: true },

  // Balconies
  { key: 'rodu', condition: BUILDING, group: true, masterEditable: true },
  { key: 'rodu_vordlus', condition: BUILDING, editable: true },
  { key: 'rodu_kohandus', condition: BUILDING, editable: true, numeric: true, kohandus: true },

  // Kitchen furniture
  { key: 'koogimoobel', condition: BUILDING, group: true, editable: true, masterEditable: true },
  { key: 'koogimoobel_vordlus', condition: BUILDING, editable: true },
  { key: 'koogimoobel_kohandus', condition: BUILDING, editable: true, numeric: true, kohandus: true },

  // Parking
  { key: 'kinnistu_parkimine', condition: BUILDING, group: true, masterEditable: true },
  { key: 'kinnistu_parkimine_vordlus', condition: BUILDING, editable: true },
  { key: 'kinnistu_parkimine_kohandus', condition: BUILDING, editable: true, numeric: true, kohandus: true },

  // Storage
  { key: 'panipaik', condition: BUILDING, group: true, masterEditable: true },
  { key: 'panipaik_vordlus', condition: BUILDING, editable: true },
  { key: 'panipaik_kohandus', condition: BUILDING, editable: true, numeric: true, kohandus: true },

  // Other info
  { key: 'muu_info', condition: ALL, group: true, editable: true, masterEditable: true },
  { key: 'muu_info_vordlus', condition: ALL, editable: true },
  { key: 'muu_info_kohandus', condition: ALL, editable: true, numeric: true, kohandus: true },

  // Residual: the leftover ML "Unknown" adjustment bucket (legacy rows only).
  // Non-editable, summed into sum_kohandus, and rendered only when non-zero
  // (see comparisonTableRows). Keeps the table total reconciled to the stored
  // display_price without mutating the prediction.
  // See memory: project_ml_unknown_grouping_bucket.
  { key: 'maaramata_kohandus', condition: ALL, numeric: true, kohandus: true, group: true },

  // Summary (always)
  { key: 'sum_kohandus', condition: ALL, group: true },
  { key: 'kohandatud_hind', condition: ALL, group: true },
  { key: 'kohanduse_abs', condition: ALL },
  { key: 'kaalud', condition: ALL, editable: true },
  { key: 'kaalutud_keskmine', condition: ALL, group: true },
  { key: 'turuvaartus', condition: ALL, group: true },
]

/**
 * Fields that are always numeric regardless of condition filtering.
 * These are "structural" numeric fields that don't come from the registry's
 * numeric flag (which is for kohandus fields).
 */
const ALWAYS_NUMERIC = [
  'tehingusumma',
  'm2hind',
  'sisaldab_km',
  'ajal_kohandus',
  'ajal_hind',
  'korteri_pind',
  'hooneosa_pind',
  // RED-313: the other two area substrates a master edit can land on
  // (land base + the RED-235 SNP half) — without this they were saved as
  // strings and skipped the numeric guard.
  'maatuki_pindala',
  'ehitise_suletud_netopind',
  // …and the derived cell itself, so the table's numeric gate covers the
  // subject's editable "Vara pindala" (its edit is routed to the base).
  'vara_pind',
  'hooneosa_tubade_arv',
  'ehitise_esmane_kasutus',
  'sum_kohandus',
  'kohandatud_hind',
  'kohanduse_abs',
  'kaalud',
  'kaalutud_keskmine',
  'turuvaartus',
  'turuvaartus_umardatud',
]

/**
 * The single place that maps evaluation state to a property context.
 *
 * @param {Object} options
 * @param {boolean} options.isLandParcel - object_type === 'cadaster-id'
 * @param {boolean} options.hasBuildingPart - a building part is selected
 * @returns {'apartment'|'built'|'land'}
 */
export function resolveComparisonContext({ isLandParcel = false, hasBuildingPart = true } = {}) {
  if (isLandParcel) return 'land'
  return hasBuildingPart ? 'apartment' : 'built'
}

/**
 * The unit-price base a fresh comparison state starts from. Land prices are
 * per plot-m² (RED-267); everything else per heated/apartment area.
 *
 * @param {'apartment'|'built'|'land'} context
 * @returns {'maatuki_pindala'|'hooneosa_pind'}
 */
export function defaultUnitPriceBase(context) {
  return context === 'land' ? 'maatuki_pindala' : 'hooneosa_pind'
}

/**
 * Build comparison table config filtered by property context.
 *
 * @param {Object} options
 * @param {boolean} options.hasBuildingPart - Whether a building part is selected (apartment)
 * @param {'apartment'|'built'|'land'} [options.context] - Explicit context; wins over hasBuildingPart
 * @returns {Object} markedTableConf-compatible config
 */
export function buildComparisonTableConfig({ hasBuildingPart = true, context } = {}) {
  const resolved = context ?? resolveComparisonContext({ hasBuildingPart })

  const applicable = COMPARISON_FIELD_REGISTRY.filter(f =>
    (CONDITION_CONTEXTS[f.condition] ?? []).includes(resolved),
  )

  const kohandusFields = applicable.filter(f => f.kohandus).map(f => f.key)

  return {
    context: resolved,
    fields: applicable.map(f => f.key),
    editables: applicable.filter(f => f.editable).map(f => f.key),
    master_editables: applicable.filter(f => f.masterEditable).map(f => f.key),
    groups: applicable.filter(f => f.group).map(f => f.key),
    numeric: [...new Set([...ALWAYS_NUMERIC, ...applicable.filter(f => f.numeric).map(f => f.key)])],
    kohandus: kohandusFields,
    kohandus_abs: ['ajal_kohandus', ...kohandusFields],
    user: {},
  }
}
